import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { adminService } from '../../../services/adminService';

const STRATEGY_STRIKES: Record<string, Record<string, number>> = {
  iron_condor: { short_call: 0, long_call: 0, short_put: 0, long_put: 0 },
  bear_call_spread: { short_call: 0, long_call: 0 },
  bull_put_spread: { short_put: 0, long_put: 0 },
  long_put_calendar: { strike: 0, far_expiry: '' as any },
  call_calendar: { strike: 0, far_expiry: '' as any },
};

const PAPER_TRADE_JOBS = [
  { id: 'paper-trade-open', label: 'Open Paper Trades', description: 'Opens paper trades for today\'s earnings with executable strategies. Runs daily at 14:30 ET.' },
  { id: 'paper-trade-close-noon', label: 'Noon Close', description: 'Partial-closes open paper trades (first half). Runs daily at 12:00 ET.' },
  { id: 'paper-trade-close-friday', label: 'Friday Close', description: 'Closes remaining half of partial paper trades. Runs Fridays at 15:00 ET.' },
] as const;

const PaperTradeAdmin: React.FC = () => {
  const [runningJob, setRunningJob] = useState<string | null>(null);
  const [jobResults, setJobResults] = useState<Record<string, 'ok' | 'error'>>({});

  // Backfill form
  const [ticker, setTicker] = useState('');
  const [earningsDate, setEarningsDate] = useState('');
  const [strategyType, setStrategyType] = useState('iron_condor');
  const [strikes, setStrikes] = useState(JSON.stringify(STRATEGY_STRIKES['iron_condor'], null, 2));

  // Update strikes template when strategy changes
  useEffect(() => {
    const template = STRATEGY_STRIKES[strategyType];
    if (template) {
      setStrikes(JSON.stringify(template, null, 2));
    }
  }, [strategyType]);
  const [expiry, setExpiry] = useState('');
  const [contracts, setContracts] = useState('2');
  const [entryPrice, setEntryPrice] = useState('');
  const [entryTime, setEntryTime] = useState('');
  const [close1Price, setClose1Price] = useState('');
  const [close1Time, setClose1Time] = useState('');
  const [close2Price, setClose2Price] = useState('');
  const [close2Time, setClose2Time] = useState('');
  const [status, setStatus] = useState('open');
  const [realizedPnl, setRealizedPnl] = useState('');
  const [spreadWidth, setSpreadWidth] = useState('');
  const [capitalAtRisk, setCapitalAtRisk] = useState('');
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState<string | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  const handleRunJob = async (jobId: string) => {
    setRunningJob(jobId);
    setJobResults((prev) => { const n = { ...prev }; delete n[jobId]; return n; });
    try {
      await adminService.runPaperTradeJob(jobId);
      setJobResults((prev) => ({ ...prev, [jobId]: 'ok' }));
    } catch {
      setJobResults((prev) => ({ ...prev, [jobId]: 'error' }));
    } finally {
      setRunningJob(null);
    }
  };

  const handleBackfill = async () => {
    setBackfillLoading(true);
    setBackfillResult(null);
    setBackfillError(null);
    try {
      let parsedStrikes: Record<string, number | string>;
      try {
        parsedStrikes = JSON.parse(strikes);
      } catch {
        setBackfillError('Invalid JSON for strikes');
        setBackfillLoading(false);
        return;
      }

      const payload = {
        ticker: ticker.toUpperCase(),
        earnings_date: earningsDate,
        strategy_type: strategyType,
        strikes: parsedStrikes,
        expiry,
        contracts: Number(contracts),
        entry_price: entryPrice ? Number(entryPrice) : null,
        entry_time: entryTime || null,
        close1_price: close1Price ? Number(close1Price) : null,
        close1_time: close1Time || null,
        close1_contracts: 1,
        close2_price: close2Price ? Number(close2Price) : null,
        close2_time: close2Time || null,
        close2_contracts: 1,
        status,
        realized_pnl: realizedPnl ? Number(realizedPnl) : null,
        spread_width: spreadWidth ? Number(spreadWidth) : null,
        capital_at_risk: capitalAtRisk ? Number(capitalAtRisk) : null,
      };
      const result = await adminService.backfillPaperTrade(payload);
      setBackfillResult(`Paper trade created (id: ${result.id})`);
    } catch (e: any) {
      setBackfillError(e.message);
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 680 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Paper Trade Admin</Typography>

      {/* Job triggers */}
      <Typography variant="h6" fontWeight={600} mt={2} mb={1}>Job Triggers</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {PAPER_TRADE_JOBS.map(({ id, label, description }) => (
          <Card variant="outlined" key={id}>
            <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>{label}</Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>{description}</Typography>
                {jobResults[id] === 'ok' && <Alert severity="success" sx={{ mt: 1, py: 0 }}>Completed successfully</Alert>}
                {jobResults[id] === 'error' && <Alert severity="error" sx={{ mt: 1, py: 0 }}>Job failed — check server logs</Alert>}
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={runningJob === id ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                disabled={!!runningJob}
                onClick={() => handleRunJob(id)}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {runningJob === id ? 'Running...' : 'Run Now'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Backfill form */}
      <Typography variant="h6" fontWeight={600} mb={2}>Backfill Paper Trade</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Ticker" value={ticker} onChange={(e) => setTicker(e.target.value)} sx={{ width: 120 }} />
          <TextField size="small" label="Earnings Date" type="date" value={earningsDate} onChange={(e) => setEarningsDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField
            size="small"
            label="Strategy"
            select
            value={strategyType}
            onChange={(e) => setStrategyType(e.target.value)}
            sx={{ width: 180 }}
            SelectProps={{ native: true }}
          >
            <option value="iron_condor">Iron Condor</option>
            <option value="bear_call_spread">Bear Call Spread</option>
            <option value="bull_put_spread">Bull Put Spread</option>
            <option value="long_put_calendar">Put Calendar</option>
            <option value="call_calendar">Call Calendar</option>
          </TextField>
        </Box>
        <TextField size="small" label="Strikes (JSON)" value={strikes} onChange={(e) => setStrikes(e.target.value)} fullWidth multiline rows={2} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Contracts" type="number" value={contracts} onChange={(e) => setContracts(e.target.value)} sx={{ width: 100 }} />
          <TextField size="small" label="Entry Price" type="number" value={entryPrice} onChange={(e) => setEntryPrice(e.target.value)} sx={{ width: 130 }} inputProps={{ step: 0.01 }} />
          <TextField size="small" label="Spread Width" type="number" value={spreadWidth} onChange={(e) => setSpreadWidth(e.target.value)} sx={{ width: 130 }} inputProps={{ step: 0.5 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Entry Time" type="datetime-local" value={entryTime} onChange={(e) => setEntryTime(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Close1 Price" type="number" value={close1Price} onChange={(e) => setClose1Price(e.target.value)} sx={{ width: 130 }} inputProps={{ step: 0.01 }} />
          <TextField size="small" label="Close1 Time" type="datetime-local" value={close1Time} onChange={(e) => setClose1Time(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Close2 Price" type="number" value={close2Price} onChange={(e) => setClose2Price(e.target.value)} sx={{ width: 130 }} inputProps={{ step: 0.01 }} />
          <TextField size="small" label="Close2 Time" type="datetime-local" value={close2Time} onChange={(e) => setClose2Time(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField
            size="small"
            label="Status"
            select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ width: 130 }}
            SelectProps={{ native: true }}
          >
            <option value="open">Open</option>
            <option value="partial">Partial</option>
            <option value="closed">Closed</option>
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Realized PnL" type="number" value={realizedPnl} onChange={(e) => setRealizedPnl(e.target.value)} sx={{ width: 130 }} inputProps={{ step: 1 }} />
          <TextField size="small" label="Capital at Risk" type="number" value={capitalAtRisk} onChange={(e) => setCapitalAtRisk(e.target.value)} sx={{ width: 150 }} inputProps={{ step: 1 }} />
        </Box>

        {backfillResult && <Alert severity="success">{backfillResult}</Alert>}
        {backfillError && <Alert severity="error">{backfillError}</Alert>}

        <Button
          variant="contained"
          onClick={handleBackfill}
          disabled={backfillLoading || !ticker || !earningsDate || !expiry}
          sx={{ alignSelf: 'flex-start' }}
        >
          {backfillLoading ? <CircularProgress size={20} /> : 'Create Paper Trade'}
        </Button>
      </Box>
    </Box>
  );
};

export default PaperTradeAdmin;
